import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DfsService } from '../../services/dfs.service';
import { User, UserInfo } from '../../models/user.models';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LoginService } from '../../services/login.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent {

  // User Details Form
  userForm: FormGroup;

  // User Details
  user: UserInfo | null = null;

  // Flags for UI state
  isLoading: boolean = true;
  isEditing: boolean = false;
  errorMessage: string = '';

  constructor(
    private loginService: LoginService,
    private dfsService: DfsService,
    private fb: FormBuilder,
    private util: UtilService
  ) {
    // Initialize the user form
    this.userForm = this.fb.group({
      Firstname: [{ value: '', disabled: true }, Validators.required],
      Lastname: [{ value: '', disabled: true }, Validators.required],
      Phone: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      Email: [{ value: '', disabled: true }, [Validators.required, Validators.email]]
    });
  }

  ngOnInit(): void {
    this.fetchUserInfo();
  }

  fetchUserInfo(): void {
    this.loginService.loginUserInfo.subscribe({
      next: (response: UserInfo | null) => {
        if (response) {
          this.user = response;

          // Populate the form
          this.userForm.patchValue({
            Firstname: this.user.Firstname,
            Lastname: this.user.Lastname,
            Phone: this.user.Phone,
            Email: this.user.Email
          });
        } else {
          this.user = null;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching user info:', error);
        this.errorMessage = 'Failed to load user information. Please try again later.';
        this.isLoading = false;
      }
    });
  }

  // Toggle edit mode
  toggleEdit(): void {
    this.isEditing = !this.isEditing;
    if (this.isEditing) {
      this.userForm.get('Firstname')?.enable();
      this.userForm.get('Lastname')?.enable();
      this.userForm.get('Phone')?.enable();
    } else {
      this.userForm.get('Firstname')?.disable();
      this.userForm.get('Lastname')?.disable();
      this.userForm.get('Phone')?.disable();
      // Optionally, reset form values to original user data
      if (this.user) {
        this.userForm.patchValue({
          Firstname: this.user.Firstname,
          Lastname: this.user.Lastname,
          Phone: this.user.Phone,
          Email: this.user.Email
        });
      }
    }
  }

  // Save updated user details
  saveUserDetails(): void {
    if (this.userForm.valid && this.user) {
      const updatedUser: User = {
        firstname: this.userForm.value.Firstname,
        lastname: this.userForm.value.Lastname,
        phone: this.userForm.value.Phone,
        email: this.user.Email
      };

      // Call the update service
      this.dfsService.editUserInfo(updatedUser).subscribe({
        next: (response) => {
          // Update local user data
          this.isEditing = false;
          // Disable form fields
          this.userForm.get('Firstname')?.disable();
          this.userForm.get('Lastname')?.disable();
          this.userForm.get('Phone')?.disable();
          
          // Show success message
          this.util.openSnackBar('Profile updated successfully!', 'Close', 'success');
          
          // Refresh user info
          this.fetchUserInfo();
        },
        error: (error) => {
          console.error('Error updating user details:', error);
          this.util.openSnackBar('Failed to update profile. Please try again.', 'Close', 'error');
        }
      });
    }
  }
}
